

'use client';
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import getSymbolFromCurrency from "currency-symbol-map";
import { useNavigate } from "react-router-dom";

const GuestProfile = () => {
    const [guestInfo, setGuestInfo] = useState(null);
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState("orders");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");

        if (token) {
            sessionStorage.setItem("guestOrderToken", token);
            console.log("Token saved:", token);
        }
    }, []);

    useEffect(() => {
        const token = typeof window !== 'undefined' ? sessionStorage.getItem("guestOrderToken") : null;
        if (token) {
            fetchGuestOrders(token);
        } else {
            toast.error("No guest session found. Please provide a valid token.");
            setTimeout(() => {
                navigate("/");
            }, 1500);
        }
    }, []);

    const fetchGuestOrders = async (token) => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URI}/guest/orders`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.orders && response.data.orders.length > 0) {
                const transformedOrders = response.data.orders.map((order) => {
                    let products = [];
                    try {
                        products = typeof order.products === 'string'
                            ? JSON.parse(order.products)
                            : order.products;
                    } catch (e) {
                        console.error("Error parsing products:", e);
                        products = [];
                    }

                    return {
                        orderId: order.order_id,
                        totalPrice: parseFloat(order.total_price),
                        currency_code: products[0]?.currency_code || "PKR",
                        status: order.status,
                        orderDate: new Date(order.order_date),
                        email: order.email,
                        fullName: order.full_name,
                        phone: order.phone,
                        country: order.country,
                        postalCode: order.postal_code,
                        state: order.state,
                        city: order.city,
                        street: order.street,
                        items: products.map((product) => ({
                            name: product.product_name,
                            price: `${parseFloat(product.price).toFixed(2)}`,
                            currency_code: product.currency_code || "PKR",
                            quantity: product.quantity,
                            image: product.image
                                ? `${process.env.REACT_APP_API_URI}/uploads/${product.image}`
                                : '/product-placeholder.jpg',
                            description: product.description,
                            color: product.units?.color || "N/A",
                            size: product.units?.size || "N/A",
                        })),
                    };
                });

                setOrders(transformedOrders);

                if (transformedOrders.length > 0) {
                    setGuestInfo({
                        fullName: transformedOrders[0].fullName,
                        email: transformedOrders[0].email,
                        phone: transformedOrders[0].phone,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching guest orders:", error);
            toast.error("Failed to load orders. Please check your token.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConvertToAccount = () => {
        navigate("/convert-guest");
    };

    const clearGuestSession = () => {
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem("guestOrderToken");
        }
        toast.success("Guest session cleared!");
        setTimeout(() => {
            window.location.href = "/";
        }, 800);
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] pt-10">
            <div className="max-w-[1400px] mx-auto p-8 pt-[7rem]">
                <div className="bg-white rounded-[20px] p-8 shadow-lg mb-8">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="relative">
                            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-[#ECF3FE] to-[#FDFEFF] border-4 border-white shadow-xl flex items-center justify-center">
                                <span className="text-4xl font-semibold text-[#f36812]">
                                    {guestInfo?.fullName?.charAt(0)?.toUpperCase() || "G"}
                                </span>
                            </div>
                        </div>
                        <div className="text-center md:text-left">
                            <h1 className="text-3xl font-semibold text-[#1E293B] mb-2">
                                {guestInfo?.fullName || "Guest User"}
                            </h1>
                            <p className="text-[#64748B]">{guestInfo?.email || "Guest account"}</p>
                            <div className="mt-1 flex items-center gap-2 justify-center md:justify-start">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#f36812] text-white">
                                    Guest Account
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 ml-auto">
                            <button
                                onClick={handleConvertToAccount}
                                className="px-6 py-2 bg-[#f36812] text-white rounded-full font-medium hover:bg-[#e55a00] transition-all duration-300 flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                Create Account
                            </button>
                            <button
                                onClick={clearGuestSession}
                                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-400 transition-all duration-300 flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                </svg>
                                Clear Session
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-center md:justify-start gap-6 mt-8">
                        {["orders", "profile"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-6 py-3 text-lg font-medium transition-all duration-300 ${activeTab === tab
                                    ? "text-[#f36812]"
                                    : "text-[#64748B] hover:text-[#f36812]"
                                    }`}
                            >
                                {tab === "orders" ? "My Orders" : "Guest Information"}
                                {activeTab === tab && (
                                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#f36812] rounded-full" />
                                )}
                                {tab === "orders" && orders.length > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-[#f36812] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                        {orders.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="bg-white rounded-[20px] p-8 shadow-lg">
                    {activeTab === "profile" ? (
                        <div className="space-y-8">
                            <h2 className="text-2xl font-semibold text-[#1E293B] mb-6">
                                Guest Information
                            </h2>
                            {guestInfo ? (
                                <div className="grid grid-cols-1 gap-8">
                                    <div className="bg-[#F8FAFC] rounded-[20px] p-6 hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className="w-12 h-12 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                                <svg
                                                    className="w-6 h-6 text-[#f36812]"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-semibold text-[#1E293B]">
                                                    Contact Details
                                                </h3>
                                                <p className="text-[#64748B] text-sm">
                                                    Your personal information
                                                </p>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <InfoRow
                                                label="Full Name"
                                                value={guestInfo.fullName || "Not provided"}
                                            />
                                            <InfoRow
                                                label="Email"
                                                value={guestInfo.email || "Not provided"}
                                            />
                                            <InfoRow
                                                label="Phone"
                                                value={guestInfo.phone || "Not provided"}
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-[#F8FAFC] rounded-[20px] p-6 hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className="w-12 h-12 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                                <svg
                                                    className="w-6 h-6 text-[#f36812]"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-semibold text-[#1E293B]">
                                                    Account Benefits
                                                </h3>
                                                <p className="text-[#64748B] text-sm">
                                                    Upgrade to a full account to enjoy these benefits
                                                </p>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3">
                                                <svg className="h-5 w-5 text-[#f36812]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-[#1E293B]">Save your order history permanently</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <svg className="h-5 w-5 text-[#f36812]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-[#1E293B]">Track orders easily</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <svg className="h-5 w-5 text-[#f36812]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-[#1E293B]">Faster checkout process</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <svg className="h-5 w-5 text-[#f36812]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-[#1E293B]">Save multiple addresses</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleConvertToAccount}
                                            className="mt-6 px-6 py-2 bg-[#f36812] text-white rounded-full font-medium hover:bg-[#e55a00] transition-all duration-300"
                                        >
                                            Create Account Now
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <EmptyState message="No guest information available" />
                            )}
                        </div>
                    ) : (
                        <div className="space-y-8">
                            <h2 className="text-2xl font-semibold text-[#1E293B] mb-6">
                                Order History
                            </h2>
                            {isLoading ? (
                                <LoadingState />
                            ) : orders.length > 0 ? (
                                <div className="space-y-6">
                                    {orders.map((order) => (
                                        <div
                                            key={order.orderId}
                                            className="bg-[#F8FAFC] rounded-[20px] p-6 hover:shadow-lg transition-all duration-300"
                                        >
                                            {/* Order Header */}
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                                        <svg
                                                            className="w-6 h-6 text-[#f36812]"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-semibold text-[#1E293B]">
                                                            Order #{order.orderId}
                                                        </h3>
                                                        <p className="text-[#64748B] text-sm">
                                                            Placed on{" "}
                                                            {order.orderDate.toLocaleDateString("en-US", {
                                                                month: "long",
                                                                day: "numeric",
                                                                year: "numeric",
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span
                                                        className={`px-4 py-1 rounded-full text-sm font-medium ${order.status === "pending" || order.status === "Pending"
                                                            ? "bg-yellow-500 text-white"
                                                            : order.status === "processing" || order.status === "Processing"
                                                                ? "bg-blue-500 text-white"
                                                                : "bg-green-500 text-white"
                                                            }`}
                                                    >
                                                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                    </span>
                                                    <span className="text-xl font-semibold text-[#1E293B]">
                                                        {getSymbolFromCurrency(order.currency_code)}{" "}
                                                        {order.totalPrice.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Items Section */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                {order.items.map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center gap-4 bg-white rounded-xl p-4"
                                                    >
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            className="w-20 h-20 object-cover rounded-xl"
                                                        />
                                                        <div>
                                                            <h4 className="font-medium text-[#1E293B] mb-1">{item.name}</h4>
                                                            {/* <p className="text-sm text-gray-500 mt-1">
                                                                <span className="font-medium text-gray-700">Color:</span>{" "}
                                                                {item.color}
                                                            </p> */}
                                                            <p className="text-sm text-gray-500 mt-1">
                                                                <span className="font-medium text-gray-700">Militer:</span>{" "}
                                                                {item.size}
                                                            </p>
                                                            <p className="text-[#64748B]">
                                                                {getSymbolFromCurrency(item.currency_code)} {item.price} x{" "}
                                                                {item.quantity}
                                                            </p>
                                                            <p className="text-[#64748B] text-sm">{item.description}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Shipping Address Section */}
                                            <div className="bg-[#F8FAFC] rounded-[20px] p-6 hover:shadow-lg transition-all duration-300 mb-6">
                                                <div className="flex items-center gap-4 mb-6">
                                                    <div className="w-12 h-12 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                                        <svg
                                                            className="w-6 h-6 text-[#f36812]"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-semibold text-[#1E293B]">
                                                            Shipping Address
                                                        </h3>
                                                        <p className="text-[#64748B] text-sm">Your delivery information</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <InfoRow label="Full Name" value={order.fullName || "N/A"} />
                                                    <InfoRow label="Email" value={order.email || "N/A"} />
                                                    <InfoRow label="Phone" value={order.phone || "N/A"} />
                                                    <InfoRow label="Country" value={order.country || "N/A"} />
                                                    <InfoRow label="City" value={order.city || "N/A"} />
                                                    <InfoRow label="State" value={order.state || "N/A"} />
                                                    <InfoRow label="Postal Code" value={order.postalCode || "N/A"} />
                                                    <InfoRow label="Street" value={order.street || "N/A"} />
                                                </div>
                                            </div>

                                            {/* Cancel Button */}
                                            <div className="flex justify-end">
                                                <button
                                                    onClick={() => toast.success("We've received your cancellation request")}
                                                    className="px-6 py-2 bg-red-500 text-white rounded-full font-medium hover:bg-red-600 transition-all duration-300"
                                                >
                                                    Cancel Order
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <EmptyState message="No orders found" />
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Toaster />
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0] last:border-0 flex-wrap">
        <span className="text-[#64748B]">{label}</span>
        <span className="font-medium text-[#1E293B] break-words">{value}</span>
    </div>
);

const EmptyState = ({ message }) => (
    <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EFF6FF] flex items-center justify-center">
            <svg
                className="w-8 h-8 text-[#f36812]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        </div>
        <p className="text-[#64748B]">{message}</p>
    </div>
);

const LoadingState = () => (
    <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EFF6FF] flex items-center justify-center animate-pulse">
            <svg
                className="w-8 h-8 text-[#f36812]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
            </svg>
        </div>
        <p className="text-[#64748B] animate-pulse">Loading orders...</p>
    </div>
);

export default GuestProfile;